import '@app/main/widgets/Schedule';

import styles from './App.module.scss';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, RouterProvider, Routes } from 'react-router-dom';
import { store } from './store';
import { useRouter } from './pages';
import { CodedException, I18n, AppSettings, isRtkqNotifiedError, openid } from '@app/base';
import {
  AntdConfigProvider,
  LocaleContextProvider,
  AutoLogout,
  ConfirmationGlobal,
  Notify,
  Spinner,
  notify,
  useSpin,
  useSyncLocale,
  ReportProblemButton,
  Problem,
  ShortcutsProvider,
  LocalStorageShortcutsPersistence,
} from '@app/ui';
import { AuthenticationProvider } from './AuthenticationProvider';
import { ProviderSignUpPage } from './pages/Providers.SignUp';
import { LanguageSwitcher } from './widgets';
import { memo, useEffect, useMemo, useRef } from 'react';
import { chatifyApi } from '@webinex/chatify';
import { useGetContextQuery, useGetUiSettingsQuery, useSaveLanguageSettings } from '@app/main/api';
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import ReactDOM from 'react-dom/client';
import { wispoClient } from '@app/notifications/api';
import { ScheduleTheme } from '@app/main/widgets/Schedule';
import { UiThemeProvider, LocalStorageThemePersistence } from '@app/ui';
import { uiThemes } from '@app/common/utils';
import { useServiceWorker } from './useServiceWorker';
import { AppProblemProvider } from './AppProblemProvider';
import { notification } from 'antd';
import i18next from 'i18next';

AutoLogout.Settings.timeout = Number(import.meta.env.VITE_AUTLOGOUT_TIMEOUT);
AutoLogout.Settings.popupBefore = Number(import.meta.env.VITE_AUTLOGOUT_POPUP_BEFORE);

const UI_THEME_LOCAL_STORAGE_KEY = `${import.meta.env.VITE_PRODUCT_CODE}://uiTheme`;
const SHORTCUTS_LOCAL_STORAGE_KEY = `${import.meta.env.VITE_PRODUCT_CODE}://shortcutsEnabled`;

const render = (Root: React.ComponentType) => {
  const domElement = ReactDOM.createRoot(document.getElementById('root')!);

  domElement.render(
    <LocaleContextProvider>
      <UiThemeProvider
        themes={uiThemes}
        default="default"
        persistence={new LocalStorageThemePersistence(UI_THEME_LOCAL_STORAGE_KEY)}
      >
        <AntdConfigProvider>
          <HelmetProvider>
            <ConfirmationGlobal />
            <Provider store={store}>
              <Notify />
              <AppProblemProvider>
                <ShortcutsProvider
                  persistence={new LocalStorageShortcutsPersistence(SHORTCUTS_LOCAL_STORAGE_KEY)}
                  defaultEnabled={false}
                >
                  <ScheduleTheme>
                    <Root />
                  </ScheduleTheme>
                </ShortcutsProvider>
              </AppProblemProvider>
            </Provider>
          </HelmetProvider>
        </AntdConfigProvider>
      </UiThemeProvider>
    </LocaleContextProvider>,
  );
};

const Pages = memo(() => {
  const router = useRouter();
  return <RouterProvider router={router} />;
});

function App() {
  const { data: context, isLoading: isContextLoading } = useGetContextQuery();
  const { isLoading: isSettingsLoading, data: uiSettings } = useGetUiSettingsQuery(undefined, {
    skip: isContextLoading,
  });
  useServiceWorker();
  const wispoConnected = useRef(false);

  useSpin(isContextLoading || isSettingsLoading);

  useMemo(() => {
    if (!context) {
      return;
    }

    chatifyApi.settings.me = () => context.id;
  }, [context]);

  useSaveLanguageSettings();

  useEffect(() => {
    if (!context || wispoConnected.current) {
      return;
    }

    wispoClient.connect().then(() => (wispoConnected.current = true));
  }, [context]);

  useSyncLocale(context?.locale);

  if (isContextLoading || !uiSettings) {
    return null;
  }

  return (
    <I18n language={uiSettings!.language || import.meta.env.VITE_DEFAULT_LANGUAGE}>
      <AutoLogout onLogout={openid.signout} onAutoLogout={openid.autoSignout} />
      <Pages />
    </I18n>
  );
}

function SignUpContainer(props: { Page: React.ComponentType }) {
  const { Page } = props;
  const params = new URLSearchParams(window.location.search);
  const language = params.get('lang') || import.meta.env.VITE_DEFAULT_LANGUAGE;
  useSyncLocale(language!);

  return (
    <I18n language={language!}>
      <LanguageSwitcher className={styles.languageSwitcher} />
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<Page />} />
        </Routes>
      </BrowserRouter>
    </I18n>
  );
}

export const run = async () => {
  await AppSettings.load();

  if (window.location.pathname.match(/^\/provider\/sign-up\/?$/)) {
    render(() => <SignUpContainer Page={ProviderSignUpPage} />);
    return;
  }

  render(() => (
    <>
      <Spinner />
      <AuthenticationProvider Component={App} />
    </>
  ));
};

export function notifyCoded(coded: CodedException) {
  if (coded.code === 'UNEXP') {
    notification.error({
      message: i18next.t('errors.coded.UNEXP'),
      actions: [<ReportProblemButton key="report" type="link" />],
    });
  } else {
    notify.error.coded(coded);
  }
}

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;

  if (isRtkqNotifiedError(error)) {
    return;
  }

  Problem.push(error);
  notifyCoded(CodedException.from(error));
});
